import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react";
import tailwindConfig from "@tailwind";
import clsx from "clsx";
import { useEffect, useRef, useState, type FC, type SVGProps } from "react";
import type { ComboboxProps } from "./combobox.types";
import { Item } from "./components";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Plus,
} from "lucide-react";
import { Preloader } from "../preloader";

/**
 * Combobox Component
 *
 * @author Adam Halasz
 * @category Components
 * @see ComboboxProps
 */
const Combobox: FC<ComboboxProps> = ({
  className,
  placeholder,
  size = "S",
  icon,
  autoFocus = false,
  items,
  error,
  success,
  warning,
  disabled,
  isLoading,
  description,
  label,
  query: inputQuery,
  onChange,
  onSelect,
  allowNewValues = false,
  shouldOpenOnFocus = false,
  customItem,
}) => {
  const [query, setQuery] = useState(inputQuery);
  const inputId = `input-${Date.now().toString()}-${Math.random()
    .toString(36)
    .slice(2, 9)}`;
  /**
   * When the text area has been filled out with content.
   */
  const inputRef = useRef<HTMLInputElement>(null);

  // Prefill the query if it's provided.
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = query ? query : "";
  }, [query]);

  const IconComponent = icon?.type;
  const CustomItem = customItem;

  const filteredItems =
    query && items
      ? items.filter(
          (item) =>
            typeof item.label === "string" &&
            item.label.toLowerCase().includes(query.toLowerCase())
        )
      : items ?? [];

  console.log("@filteredItems", filteredItems);
  return (
    <HeadlessCombobox
      as={"article"}
      className={"relative flex flex-col space-y-spacing-5xs"}
      immediate={shouldOpenOnFocus}
      onChange={(value: string) => {
        onSelect?.(value);
        setQuery(value);
      }}
    >
      {({ open }) => (
        <>
          {label && (
            <label
              htmlFor={inputId}
              className={"prose-paragraph-14-medium select-none"}
            >
              {label}
            </label>
          )}

          <div
            className={clsx(
              "input flex h-fit w-full flex-row  items-center justify-stretch  rounded-xs",
              {
                "!border-success-secondary hover:!border-success-secondary_hover":
                  success && !isLoading,
              },
              {
                "!border-warning-secondary hover:!border-warning-secondary_hover":
                  warning && !isLoading,
              },
              {
                "!border-danger-secondary hover:!border-danger-secondary_hover":
                  error && !isLoading,
              },

              "flex h-fit w-full flex-row items-center justify-stretch space-x-spacing-3xs rounded-xs",
              size === "S" && "px-xs py-spacing-3xs",
              size === "M" && "px-xs py-xs",
              size === "L" && "px-xs py-spacing-s",
              className
            )}
            onClick={() => inputRef.current?.focus()}
          >
            {IconComponent && (
              <IconComponent
                className={clsx(
                  "text-foreground/50 ml-xs mr-xs",
                  size === "S" && "size-4",
                  size === "L" && "size-5"
                )}
                {...(icon.props as SVGProps<SVGSVGElement>)}
              />
            )}
            <ComboboxInput
              ref={inputRef}
              autoComplete={"off"}
              autoFocus={autoFocus}
              id={inputId}
              className={clsx(
                "outline-none w-full bg-transparent",
                size == "S" && "prose-paragraph-14-medium",
                size == "M" || (size == "L" && "prose-paragraph-14-medium"),
                icon && size === "S" && "",
                icon && size === "L" && "",
                size === "L" && "prose-paragraph-14-medium",
                query &&
                  "[&_textarea]:!bg-secondary_hover [&_textarea]:hover:!bg-primary_hover"
              )}
              onChange={(event) => {
                setQuery(event.target.value);
                onChange?.(event);
              }}
              placeholder={placeholder}
            />

            <div className={clsx("flex space-x-spacing-3xs")}>
              {(error || success || warning || isLoading) && (
                <div
                  className={clsx(
                    "pointer-events-none inset-y-0 my-auto flex h-fit items-center text-quaternary transition-colors",

                    query
                      ? "text-secondary group-focus-within:text-secondary group-focus-within:group-hover:text-secondary"
                      : "text-quaternary group-focus-within:text-quaternary group-focus-within:group-hover:text-quaternary",
                    disabled &&
                      (query
                        ? "text-secondary_disabled"
                        : "text-quaternary_disabled")
                  )}
                >
                  {success && !warning && !error && !isLoading && (
                    <CheckCircle
                      className={clsx(
                        "text-success",
                        size === "S" ? "size-4" : "size-5"
                      )}
                    />
                  )}
                  {warning && !success && !error && !isLoading && (
                    <AlertTriangle
                      className={clsx(
                        "text-warning",
                        size === "S" ? "size-4" : "size-5"
                      )}
                    />
                  )}
                  {error && !success && !warning && !isLoading && (
                    <AlertCircle
                      className={clsx(
                        "text-danger",
                        size === "S" ? "size-4" : "size-5"
                      )}
                    />
                  )}
                  {isLoading && <Preloader size={size === "L" ? "L" : "S"} />}
                </div>
              )}
              {items?.length ? (
                <ComboboxButton>
                  <ChevronDown
                    className={clsx(
                      "size-4 transform transition-transform duration-200",
                      { "rotate-180": open }
                    )}
                  />
                </ComboboxButton>
              ) : undefined}
            </div>
          </div>

          {description && !error && !success && !warning && (
            <p
              className={
                "prose-caption-12-medium select-none text-sm text-tertiary"
              }
              id={"input-description"}
            >
              {description}
            </p>
          )}

          {success && !error && !warning && !isLoading && (
            <p className={"prose-caption-12-medium select-none text-success"}>
              {success}
            </p>
          )}

          {warning && !error && !success && !isLoading && (
            <p className={"prose-caption-12-medium select-none text-warning"}>
              {warning}
            </p>
          )}

          {error && !success && !warning && !isLoading && (
            <p
              className={"prose-caption-12-medium select-none text-danger"}
              id={"input-error"}
            >
              {error}
            </p>
          )}

          {items?.length ? (
            <ComboboxOptions
              className={clsx(
                "bg-background/100 border-foreground/10 absolute rounded-sm mt-sm left-0 right-0 top-full z-10 mt-spacing-3xs h-fit max-h-80 w-full border p-spacing-5xs empty:invisible overflow-x-hidden overflow-y-auto",
                "origin-top border transition duration-200 ease-out empty:invisible data-[closed]:opacity-0",
                "data-[leave]:data-[closed]:opacity-0 data-[leave]:duration-300"
              )}
              // It should not block other parts of the app like the submit button, thus we set modal to false.
              modal={false}
              transition
            >
              {/* The first element is the query the user types in, it should be hidden. */}
              {allowNewValues && filteredItems?.length === 0 && query && (
                <Item
                  key={0}
                  label={query}
                  value={query}
                  icon={<Plus className={"!size-4 text-foreground/50"} />}
                />
              )}

              {filteredItems?.length
                ? filteredItems.map((item, index) =>
                    CustomItem ? (
                      <CustomItem key={index} {...item} query={query} />
                    ) : (
                      <Item {...item} key={index + 1} query={query} />
                    )
                  )
                : !allowNewValues && (
                    <span className={"prose-paragraph-14-regular p-spacing-xs"}>
                      No results found.
                    </span>
                  )}
            </ComboboxOptions>
          ) : undefined}
        </>
      )}
    </HeadlessCombobox>
  );
};

export { Combobox };
